import type {AirtablePromotionItem} from '../models/airtable';

/**
 * Members-only access rules for promotions (DIS-1566).
 *
 * Deliberately free of store, config and transport imports so presentational components
 * (`PromotionTile`) can gate on eligibility without pulling `promotionsStore` — and its
 * `config`/`apiService` graph — into their tests.
 */

/** The Airtable status that marks a promotion's draw as complete. */
export const DRAWN_WINNERS_STATUS = 'Drawn Winners';

/** Narrowed promotion shape: only these two fields affect access. */
export type MembersOnlyGatedPromotion = Pick<AirtablePromotionItem, 'membersOnly' | 'status'>;

/** Viewer state the gate depends on. `hasRewards` is Rewards membership, not the store flag. */
export interface PromotionViewerEligibility {
  isLoggedIn: boolean;
  hasRewards: boolean;
}

/**
 * `true` when the viewer may open `promotion`. Eligibility is session + Rewards
 * membership (`isLoggedIn && hasRewards`), **not** the selected store's loyalty flag.
 *
 * `isLoggedIn` is required: persisted `hasRewards` outlives the session (DIS-1633).
 *
 * A drawn promotion is public regardless of `membersOnly`: the draw is over, so the record
 * carries a result to announce rather than an entry to protect. Every collection other than
 * `drawnWinnersPromotions` already excludes drawn records upstream, so this branch only ever
 * reaches the Winners! list and its detail screen.
 */
export const canViewMembersOnlyPromotion = (
  promotion: MembersOnlyGatedPromotion,
  {isLoggedIn, hasRewards}: PromotionViewerEligibility,
): boolean => {
  if (promotion.status === DRAWN_WINNERS_STATUS) {
    return true;
  }
  // Airtable omits unchecked checkboxes, so these flags can be `undefined` at runtime even
  // though they are typed `boolean`. Coerce so callers can pass the result to `disabled`.
  return promotion.membersOnly ? Boolean(isLoggedIn && hasRewards) : true;
};
